import { z } from 'zod';
import { assetRefSchema, captionStyleSchema, cropRectSchema } from './commands';
import { ALL_LAYOUT_PRESETS, type Edl, ensureDefaultTracks } from './edl';

/**
 * Runtime validation for a full EDL document. The server treats EDLs arriving
 * from clients as UNTRUSTED — a malicious/buggy client must not be able to
 * persist a malformed document. `parseEdl` throws on anything that isn't a
 * structurally valid EDL.
 *
 * This mirrors the types in `edl.ts`; `z.infer<typeof edlSchema>` is the Edl type
 * (asserted at the bottom of this file).
 */

const baseItem = {
  id: z.string().min(1),
  from: z.number().int().nonnegative(),
  durationInFrames: z.number().int().positive(),
};

const clipItemSchema = z.object({
  ...baseItem,
  type: z.literal('clip'),
  source: assetRefSchema,
  crop: cropRectSchema.optional(),
  fit: z.enum(['blur', 'cover', 'contain']).optional(),
  volume: z.number().min(0).max(1).optional(),
  rect: cropRectSchema.optional(),
});

const captionItemSchema = z.object({
  ...baseItem,
  type: z.literal('caption'),
  text: z.string().min(1),
  style: captionStyleSchema,
});

const transitionItemSchema = z.object({
  ...baseItem,
  type: z.literal('transition'),
  kind: z.enum(['cut', 'fade', 'whip', 'zoom']),
  afterItemId: z.string().min(1),
  beforeItemId: z.string().min(1),
});

const sfxItemSchema = z.object({
  ...baseItem,
  type: z.literal('sfx'),
  sfxId: z.string().min(1),
  volume: z.number().min(0).max(1).optional(),
});

const gifItemSchema = z.object({
  ...baseItem,
  type: z.literal('gif'),
  src: z.string().url(),
  rect: cropRectSchema,
});

const itemSchema = z.discriminatedUnion('type', [
  clipItemSchema,
  captionItemSchema,
  transitionItemSchema,
  sfxItemSchema,
  gifItemSchema,
]);

const trackSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(['video', 'caption', 'sfx', 'broll', 'overlay']),
  items: z.array(itemSchema),
});

export const edlSchema = z.object({
  version: z.literal(1),
  fps: z.literal(30),
  width: z.literal(1080),
  height: z.literal(1920),
  // Legacy EDLs (persisted before layout existed) have no `layout`; default it
  // so those documents still parse. Output is always a concrete preset.
  // 🔴 열거를 «손으로» 적지 않는다 — 목록에서 유도한다. 예전에는 여기와
  //    `commands.ts` 두 곳에 같은 배열이 박혀 있었고, 프리셋을 더하면 그 값을 가진 EDL 이
  //    검증에서 **조용히 거부**됐다. 아래 `_EdlMatchesSchema` 단정이 그 어긋남을 잡아
  //    주지만(실제로 이 프리셋을 더할 때 잡았다), 애초에 어긋날 수 없게 두는 쪽이 낫다.
  layout: z.enum(ALL_LAYOUT_PRESETS).default('basic'),
  title: z.object({ text: z.string().min(1), style: captionStyleSchema }).optional(),
  durationInFrames: z.number().int().nonnegative(),
  seq: z.number().int().positive(),
  tracks: z.array(trackSchema),
});

/**
 * 쓰기용 상한. **새로 들어오는 EDL 에만** 건다.
 *
 * 🔴 `edlSchema` 자체에는 상한을 두지 않는다. 그 스키마는 **이미 DB 에 있는 문서를
 * 읽을 때도** 쓰이므로(`parseEdl` → 에디터 로드), 거기에 상한을 걸면 예전에 저장된
 * 큰 프로젝트가 영영 안 열리게 된다. 막을 것은 «앞으로 더 커지는 것» 이다.
 *
 * 숫자 근거: 쇼츠는 3~180초다. 180초를 0.5초짜리 자막으로 꽉 채워도 360개고,
 * 트랙은 기본 6개(`DEFAULT_TRACK_IDS`)다. 아래 값은 그보다 한참 위라 정상 편집이
 * 걸릴 일이 없다. 상한이 아예 없으면 인증된 스트리머 하나가(또는 폭주한 에이전트가)
 * 자막 수십만 개짜리 문서를 저장할 수 있고, 그 뒤로 로드·익스포트·발행이 전부
 * 그 문서를 읽어 돌기 때문에 되돌릴 방법이 삭제뿐인 상태가 된다.
 */
export const EDL_LIMITS = {
  tracks: 32,
  itemsPerTrack: 2000,
  /** 자막 한 줄 · 제목의 글자 수. */
  textLength: 1000,
} as const;

/** 서버가 **받을 때** 쓰는 스키마. 읽기(`parseEdl`)에는 쓰지 않는다. */
export const writableEdlSchema = edlSchema.superRefine((edl, ctx) => {
  if (edl.tracks.length > EDL_LIMITS.tracks) {
    ctx.addIssue({
      code: 'custom',
      path: ['tracks'],
      message: `too many tracks (${edl.tracks.length} > ${EDL_LIMITS.tracks})`,
    });
  }
  if ((edl.title?.text.length ?? 0) > EDL_LIMITS.textLength) {
    ctx.addIssue({ code: 'custom', path: ['title', 'text'], message: 'title too long' });
  }
  edl.tracks.forEach((track, t) => {
    if (track.items.length > EDL_LIMITS.itemsPerTrack) {
      ctx.addIssue({
        code: 'custom',
        path: ['tracks', t, 'items'],
        message: `too many items (${track.items.length} > ${EDL_LIMITS.itemsPerTrack})`,
      });
    }
    track.items.forEach((item, i) => {
      if (item.type === 'caption' && item.text.length > EDL_LIMITS.textLength) {
        ctx.addIssue({
          code: 'custom',
          path: ['tracks', t, 'items', i, 'text'],
          message: 'caption too long',
        });
      }
    });
  });
});

/**
 * Parse + validate an untrusted value as an Edl. Throws ZodError on failure.
 *
 * 검증 직후 기본 트랙을 보강한다 — `caption-2`처럼 나중에 추가된 트랙이 없는 과거
 * 프로젝트에서 커맨드가 `TRACK_NOT_FOUND`로 죽는 것을 막는다. 보강할 게 없으면
 * 원본 참조를 그대로 반환하므로 비용이 없다.
 */
export function parseEdl(value: unknown): Edl {
  return ensureDefaultTracks(edlSchema.parse(value));
}

// Compile-time guarantee that the schema's output type matches the Edl type.
type _SchemaMatchesEdl = z.infer<typeof edlSchema> extends Edl ? true : false;
type _EdlMatchesSchema = Edl extends z.infer<typeof edlSchema> ? true : false;
const _assertOut: _SchemaMatchesEdl = true;
const _assertIn: _EdlMatchesSchema = true;
void _assertOut;
void _assertIn;
